import api from '@pray/shared/utils/api';

import type { SeedPreviewCatalogSelection, SeedPreviewResponse, SeedRunType } from './types';

const PREVIEW_URL = '/web/studio/admin/seed-emails/preview';

export type PostSeedPreviewParams = {
  type: SeedRunType;
  campaignId: string;
  /** Absent only for a transactional template, which belongs to no artist. */
  artistId?: string;
  catalogSelections?: SeedPreviewCatalogSelection[];
  replacements?: Record<string, string>;
  /** The seed the render simulates in `To` and in the address tokens. */
  sampleEmail?: string;
  /** Recurring campaigns only: which occurrence the run stands for. */
  occurrenceDate?: string;
};

/**
 * The email the backend would send, rendered by the same service that writes the run snapshot.
 *
 * The body is rendered on the server and never in the browser: only the server knows the Liquid
 * dialect, the catalogs and the sender, and a body assembled here would promise an email the send
 * would not reproduce.
 */
export async function postPreview({
  type,
  campaignId,
  artistId,
  catalogSelections = [],
  replacements = {},
  sampleEmail,
  occurrenceDate,
}: PostSeedPreviewParams) {
  const res = await api.executePostRequest<{ data: SeedPreviewResponse }>(PREVIEW_URL, {
    type,
    campaign_id: campaignId,
    ...(artistId ? { artist_id: artistId } : {}),
    // snake_case happens here and nowhere above it, the way `putGroup` does it: a caller holding a
    // wire-shaped object would leak that casing into the draft and the query key.
    catalog_selections: catalogSelections.map((selection) => ({
      slug: selection.slug,
      catalog_id: selection.catalogId,
      key: selection.key,
    })),
    replacements,
    ...(sampleEmail ? { sample_email: sampleEmail } : {}),
    ...(occurrenceDate ? { occurrence_date: occurrenceDate } : {}),
  });

  return res.data.data;
}
